import { Icon, combineClass, type JSX } from 'cdui-js';
import './difi-nav-bar.css';
import { SolidArrowLeftIcon } from '../../../pages/mobile/icons';

const BG_COLOR = 'transparent'

export type DiFiNavBarProps = {
  /** 标题，支持字符串或自定义节点 */
  title?: string | JSX.Element;
  /** 左侧文案 */
  leftText?: string;
  /** 右侧文案 */
  rightText?: string;
  /** 禁用左侧，降低透明度且不可点击 */
  leftDisabled?: boolean;
  /** 禁用右侧 */
  rightDisabled?: boolean;
  /** 是否显示左侧返回箭头 */
  leftArrow?: boolean;
  /** 是否显示底部分割线 */
  border?: boolean;
  /** 背景颜色 */
  bgColor?: string;
  /** 是否固定在顶部 */
  fixed?: boolean;
  /** fixed 为 true 时是否在文档流中占位，避免内容被遮挡 */
  placeholder?: boolean;
  /** 层级 */
  zIndex?: number | string;
  /** 顶部安全区（刘海屏等） */
  safeAreaInsetTop?: boolean;
  /** 两侧是否有点击态反馈 */
  clickable?: boolean;
  /** 点击左侧 */
  onClickLeft?: (e: MouseEvent) => void;
  /** 点击右侧 */
  onClickRight?: (e: MouseEvent) => void;
  /** 自定义左侧整体内容（优先于 leftArrow + leftText） */
  left?: JSX.Element;
  /** 自定义右侧整体内容（优先于 rightText） */
  right?: JSX.Element;
  /** 追加到根节点，与默认 `difi-nav-bar` 等 class 合并 */
  class?: string;
  style?: JSX.CSSProperties;
};

const BAR_HEIGHT = '54px';

const mergeZIndex = (z: number | string | undefined) => {
  if (z === undefined) return 1;
  return typeof z === 'number' ? z : z;
};

const isStringTitle = (t: DiFiNavBarProps['title']): t is string => typeof t === 'string';

export const DiFiNavBar = (rawProps: DiFiNavBarProps) => {
  const leftText = rawProps.leftText ?? '';
  const rightText = rawProps.rightText ?? '';
  const leftDisabled = rawProps.leftDisabled === true;
  const rightDisabled = rawProps.rightDisabled === true;
  const leftArrow = rawProps.leftArrow ?? true;
  const border = rawProps.border ?? false;
  const fixed = rawProps.fixed === true;
  const placeholder = rawProps.placeholder === true;
  const zIndex = mergeZIndex(rawProps.zIndex);
  const safeAreaInsetTop = rawProps.safeAreaInsetTop === true;
  const clickable = rawProps.clickable !== false;

  const bgColor = rawProps.bgColor ?? null;

  const paddingTop = safeAreaInsetTop ? 'env(safe-area-inset-top, 0px)' : undefined;
  const placeholderHeight =
    safeAreaInsetTop ? `calc(${BAR_HEIGHT} + env(safe-area-inset-top, 0px))` : BAR_HEIGHT;

  const rootStyle: JSX.CSSProperties = {
    ...(fixed
      ? {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
      }
      : {}),
    'z-index': zIndex,
    ...(paddingTop ? { 'padding-top': paddingTop } : {}),
    ...rawProps.style,
    'background-color': bgColor ? bgColor : null,
  };

  const onLeftClick = (e: MouseEvent) => {
    if (leftDisabled) return;
    rawProps.onClickLeft?.(e);
  };

  const onRightClick = (e: MouseEvent) => {
    if (rightDisabled) return;
    rawProps.onClickRight?.(e);
  };

  const sideClass = (disabled: boolean) =>
    [
      'difi-nav-bar__side flex-row align-center justify-start gap-8px',
      disabled ? 'difi-nav-bar__side--disabled' : '',
      !disabled && clickable ? 'difi-nav-bar__side--clickable' : '',
    ]
      .filter(Boolean)
      .join(' ');

  const renderLeft = () => {

    if (rawProps.left) {
      return (
        <div class={sideClass(leftDisabled)} role="button" tabIndex={leftDisabled ? -1 : 0} onClick={onLeftClick}>
          {rawProps.left}
        </div>
      );
    }
    // const hasSide = leftArrow || leftText !== '';
    // if (!hasSide) {
    //   return <Icon name="common-back-arrow" class={`icon-s-XL icon-c-D`} />;
    // }

    return (
      <div class={sideClass(leftDisabled)} role="button" tabIndex={leftDisabled ? -1 : 0} onClick={onLeftClick}>
        {leftArrow ? <SolidArrowLeftIcon /> : null}
        {leftText ? <span class="color-D font-s-S ">{leftText}</span> : null}
      </div>
    );
  };

  const renderRight = () => {

    if (rawProps.right) {
      return (
        <div
          class={`${sideClass(rightDisabled)} justify-end`}
          role="button"
          tabIndex={rightDisabled ? -1 : 0}
          onClick={onRightClick}
        >
          {rawProps.right}
        </div>
      );
    }
    // if (rightText === '') {
    //   return <div class="difi-nav-bar__side flex-1" />;
    // }
    return (
      <div
        class={`${sideClass(rightDisabled)} justify-end`}
        role="button"
        tabIndex={rightDisabled ? -1 : 0}
        onClick={onRightClick}
      >
        <span class="color-D font-s-S single-line-ellipsis">{rightText}</span>
        <Icon name="search" class={`icon-s-XL icon-c-D`}></Icon>
        <Icon name="mail" class={`icon-s-XL icon-c-D`}></Icon>
      </div>
    );
  };

  const renderTitleNode = () => {
    if (rawProps.title === '' || rawProps.title === undefined) {
      return null;
    }
    if (isStringTitle(rawProps.title)) {
      return <span class="color-D font-s text-center single-line-ellipsis">{rawProps.title}</span>;
    }
    return rawProps.title;
  };

  return (
    <>
      {fixed && placeholder && (
        <div
          class="difi-nav-bar__placeholder flex-shrink-0 padding-x-L"
          style={{
            height: placeholderHeight,
            width: '100%',
            'background-color': bgColor
          }}
          aria-hidden="true"
        />
      )}

      <div
        class={combineClass(
          'difi-nav-bar padding-x-L flex-shrink-0',
          border ? 'border-b border-b-c' : '',
          rawProps.class ?? '',
        )}
        style={rootStyle}
      >
        <div
          class="difi-nav-bar__inner flex-row align-center justify-space-between"
          style={{ 'min-height': BAR_HEIGHT, height: BAR_HEIGHT }}
        >
          {renderLeft()}
          <div class="difi-nav-bar__title flex-1 flex-row align-center justify-center padding-x-S font-bold">
            {renderTitleNode()}
          </div>
          {renderRight()}
        </div>
      </div>
    </>
  );
};
